import { useEffect, useMemo, useState, type FC } from 'react'

import { AssetFallbackIcon, Checkbox, InfoBox, LoaderCircle, Radio, RadioGroup } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { ImageWithIconFallback } from '@skytech/ui/Image'
import { useQueries } from '@tanstack/react-query'
import AlertIcon from 'mdi-react/AlertIcon'
import CartOutlineIcon from 'mdi-react/CartOutlineIcon'
import { useLocation, useNavigate } from 'react-router-dom'
import { twJoin } from 'tailwind-merge'

import queries from '@/api/queries'
import type { Policy } from '@/components/ecom-policy/types'
import { FormattedNumberWithCurrency } from '@/components/FormattedNumberWithCurrency'
import { Translation } from '@/components/i18n/Translation'
import Page from '@/components/page/Page'
import { useCart } from '@/contexts/cart/cart-context'
import { ecomPolicyResources } from '@/resources/ecom-policy'
import type { MasterOrderAssetRequest, MasterOrderRequest } from '@/resources/ecom-policy-orders'
import { APP_ROUTES } from '@/util/appRoutes'

export const CartAssets: FC = () => {
  const location = useLocation()
  const { oldAssetsState }: { oldAssetsState: MasterOrderAssetRequest[] } = location.state || {}
  const [assetsState, setAssetsState] = useState<MasterOrderAssetRequest[]>([])
  const { getPolicyIds, getItemsByPolicy } = useCart()

  const policiesQueries = useQueries({
    queries: getPolicyIds().map(policyId => ({
      queryKey: queries.policy(Number(policyId)),
      queryFn: () => ecomPolicyResources.get(Number(policyId)),
      select: (data: Policy) => {
        const items = getItemsByPolicy(policyId)
        const itemCategories = items
          .map(item => {
            if (item.product) {
              return item.product.category
            }
            if (item.asset) {
              return item.asset.classification
            }
            return undefined
          })
          .filter(Boolean) as string[]
        const policyAssets =
          data.assets?.filter(asset => itemCategories.includes(asset.classification)) || []

        return { ...data, assets: [...policyAssets] }
      },
    })),
  })

  const allSettled = policiesQueries.every(q => q.isSuccess || q.isError)

  const handleProceedToCheckout = async (assetsState: MasterOrderAssetRequest[]) => {
    navigate('/cart/confirm', { state: { assetsState: assetsState } })
  }

  useEffect(() => {
    if (allSettled) {
      // Initialize state for assets that allow buyout or return
      const initialState: MasterOrderAssetRequest[] = policiesQueries
        .flatMap(policy =>
          policy.data?.assets
            .filter(asset => Boolean(asset.isAssetReturnRequired))
            .map(asset => ({ assetId: asset.id, policyId: policy.data?.id, decision: 'NONE' }))
        )
        .filter((item): item is MasterOrderAssetRequest => item !== undefined)

      if (oldAssetsState && oldAssetsState.length > 0) {
        setAssetsState(oldAssetsState)
      } else if (initialState.length > 0) {
        setAssetsState(initialState)
      } else {
        // Navigate directly to the Checkout page if there are no assets to return
        handleProceedToCheckout([])
      }
    }
  }, [allSettled, oldAssetsState])

  const navigate = useNavigate()
  const isQueryLoading = policiesQueries.some(q => q.isLoading)
  const hasError = policiesQueries.some(q => q.error)

  const { hasAssetsToReturn, assetsToReturnOnly, assetsReturnOrBuyout } = useMemo(() => {
    const assets = policiesQueries.flatMap(q => q.data?.assets || [])
    const assetsToReturnOnly =
      assets.filter(asset => Boolean(asset.isAssetReturnRequired) && !asset.isBuyoutAllowed) || []
    const assetsReturnOrBuyout =
      assets.filter(asset => Boolean(asset.isAssetReturnRequired) && asset.isBuyoutAllowed) || []

    return {
      hasAssetsToReturn: assetsToReturnOnly.length > 0 || assetsReturnOrBuyout.length > 0,
      assetsToReturnOnly,
      assetsReturnOrBuyout,
    }
  }, [policiesQueries])

  if (isQueryLoading) {
    return <LoaderCircle />
  }

  if (hasError) {
    return null
  }

  return (
    <Page pageWrapperClassName="max-w-5xl" translate="no">
      <h2 className="sm:!text-md !m-0 !mb-3 !text-sm font-semibold max-sm:!text-left sm:!mb-4 md:!text-lg">
        <Translation id="Existing equipment" />
      </h2>

      {/* Assets only to return */}
      {assetsToReturnOnly.length > 0 && (
        <div className="mb-3 flex flex-col gap-1 sm:mb-4 sm:gap-2">
          <InfoBox emphasis="warning" className="max-sm:!p-2 max-sm:!text-sm max-sm:!leading-none">
            <AlertIcon className="!mr-2 !size-12 sm:!size-8" />
            <Translation id="Before you can create the order, you must confirm that you agree to hand in the following equipment." />
          </InfoBox>

          {assetsToReturnOnly.map(asset => {
            const assetState = assetsState.find(a => a.assetId === asset.id)
            return (
              <div
                key={asset.id}
                className={twJoin(
                  'sm:shadow-basic flex max-w-full items-center justify-between gap-2 px-0 py-2 sm:gap-3 sm:rounded-lg sm:px-4',
                  'max-sm:border-b max-sm:border-solid max-sm:border-transparent max-sm:border-b-gray-300/50 max-sm:last:border-b-transparent'
                )}
              >
                <div className="shadow-basic-black flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full">
                  <ImageWithIconFallback
                    src={asset.imgUrl || ''}
                    alt="product"
                    className="max-h-[80%]"
                    fallbackIcon={
                      <AssetFallbackIcon assetType={asset.assetType || 'OTHER'} size={1.5} />
                    }
                  />
                </div>

                <div className="flex flex-1 flex-col md:gap-0.5">
                  <div className="text-sm text-gray-500 sm:text-base md:text-lg">
                    {asset.description || <Translation id="Product Name" />}
                  </div>

                  <Checkbox
                    id={`asset-${asset.id}-return-agreement`}
                    className="!mt-1"
                    label={
                      <Translation
                        id="I agree to return the equipment to my employer"
                        assetName={asset.description || <Translation id="this product" />}
                      />
                    }
                    checked={assetState?.decision === 'RETURN'}
                    onChange={e => {
                      const decision = e.target.checked ? 'RETURN' : 'NONE'
                      const updatedState = assetsState.map(a =>
                        a.assetId === asset.id
                          ? {
                              ...a,
                              decision:
                                decision as MasterOrderRequest['assets'][number]['decision'],
                            }
                          : a
                      )
                      setAssetsState(updatedState)
                    }}
                  />
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Assets to return or buyout */}
      {assetsReturnOrBuyout.length > 0 && (
        <div className="mb-3 flex flex-col gap-1 sm:mb-4 sm:gap-2">
          <InfoBox emphasis="warning" className="max-sm:!p-2 max-sm:!text-sm max-sm:!leading-none">
            <AlertIcon className="!mr-2 !size-12 sm:!size-8" />
            <Translation id="Before you can create the order, you must decide what you want to do with the following equipment and that the cost is deducted from your salary." />
          </InfoBox>

          {assetsReturnOrBuyout.map(asset => {
            const assetState = assetsState.find(a => a.assetId === asset.id)
            const isBuyOutSelected = assetState?.decision === 'BUYOUT'
            const isReturnSelected = assetState?.decision === 'RETURN'

            return (
              <div
                key={asset.id}
                className={twJoin(
                  'sm:shadow-basic flex max-w-full items-center justify-between gap-2 px-0 py-2 sm:gap-3 sm:rounded-lg sm:px-4',
                  'max-sm:border-b max-sm:border-solid max-sm:border-transparent max-sm:border-b-gray-300/50 max-sm:last:border-b-transparent'
                )}
              >
                <div className="shadow-basic-black flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full">
                  <ImageWithIconFallback
                    src={asset.imgUrl || ''}
                    alt="product"
                    className="max-h-[80%]"
                    fallbackIcon={
                      <AssetFallbackIcon assetType={asset.assetType || 'OTHER'} size={1.5} />
                    }
                  />
                </div>

                <div className="flex flex-1 flex-col md:gap-0.5">
                  <div className="text-sm text-gray-500 sm:text-base md:text-lg">
                    {asset.description || <Translation id="Product Name" />}
                  </div>

                  {asset.isBuyoutAllowed && (
                    <RadioGroup
                      id={`asset ${asset.id}`}
                      containerClassName="flex flex-col max-sm:!gap-1 max-sm:mt-1 !items-start sm:flex-row sm:[&>*]:min-w-[200px]"
                    >
                      <Radio
                        id={`asset_${asset.id}_buyout`}
                        checked={isBuyOutSelected === true}
                        onChange={() => {
                          const updatedState = assetsState.map(a =>
                            a.assetId === asset.id
                              ? {
                                  ...a,
                                  decision:
                                    'BUYOUT' as MasterOrderRequest['assets'][number]['decision'],
                                }
                              : a
                          )
                          setAssetsState(updatedState)
                        }}
                        label={
                          <Translation
                            id="Kjøp ut, {amount}"
                            amount={
                              <em>
                                <FormattedNumberWithCurrency amount={asset.priceBuyout || 0} />
                              </em>
                            }
                          />
                        }
                      />

                      <Radio
                        id={`asset_${asset.id}_return`}
                        checked={isReturnSelected === true}
                        onChange={() => {
                          const updatedState = assetsState.map(a =>
                            a.assetId === asset.id
                              ? {
                                  ...a,
                                  decision:
                                    'RETURN' as MasterOrderRequest['assets'][number]['decision'],
                                }
                              : a
                          )
                          setAssetsState(updatedState)
                        }}
                        label={
                          <Translation
                            id="Lever inn, {amount}"
                            amount={
                              <em>
                                <FormattedNumberWithCurrency amount={asset.priceReturn || 0} />
                              </em>
                            }
                          />
                        }
                      />
                    </RadioGroup>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}

      {!hasAssetsToReturn && (
        <h3 className="!mx-0 text-center">
          <Translation id="No assets to return. You can proceed to checkout." />
        </h3>
      )}

      <div className="mobile:flex-row flex w-full flex-col items-center justify-center max-sm:mt-4 sm:gap-2 md:gap-4">
        <Button
          variant="secondary"
          className="w-full sm:w-auto"
          onClick={() => navigate(APP_ROUTES.ecomPolicyList)}
          Icon={<CartOutlineIcon />}
        >
          <Translation id="Shop more" />
        </Button>
        <Button
          variant="primary"
          className="w-full sm:w-auto"
          disabled={hasAssetsToReturn && assetsState.some(v => v.decision === 'NONE')}
          onClick={() => handleProceedToCheckout(assetsState)}
        >
          <Translation id="Proceed" />
        </Button>
      </div>
    </Page>
  )
}
